import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import produce from 'immer';
import { findDOMNode } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import { useSelector, useDispatch } from 'react-redux';
import globalNavHeight from 'nav-meta/global-nav-height';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H4 from 'UIComponents/elements/headings/H4';
import UIBanner from 'UIComponents/alert/UIBanner';
import UIButton from 'UIComponents/button/UIButton';
import UICardGrid from 'UIComponents/card/UICardGrid';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import * as RequestStates from 'ecosystem-marketplace-core/constants/RequestState';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { isFulfilled, isUninitialized } from 'ecosystem-marketplace-core/utils/requestState';
import { getCmsFreeSignupUrl } from 'ecosystem-marketplace-core/assets/utils/urls';
/* @ts-expect-error module is untyped */

import { usePrevious } from 'ecosystem-marketplace-core/hooks/usePrevious';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import { useScreenSize } from 'ecosystem-marketplace-core/hooks/useScreenSize';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
/* @ts-expect-error module is untyped */

import ScrollToResults from 'ecosystem-marketplace-core/components/storefront/ScrollToResults'; // @ts-expect-error untyped dependency

import StorefrontResultsHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontResultsHeader';
/* @ts-expect-error module is untyped */

import { updateResultsView } from '../actions/StorefrontActions';
import * as ThemeQueryField from '../constants/ThemeQueryField';
import { getComputedThemeQuery, getLocationFromThemeQuery, toFilterTags, toQueryString, toSearchQuery, toUsageEventProperties } from '../utils/themeQuery';
import { getOffsetFromPageNumber, getPaginatedPathname } from '../utils/pagination';
import { PublicAppContext } from '../selectors/app'; // @ts-expect-error module is untyped

import { selectResultsView } from '../selectors/storefront';
import { searchThemes } from '../clients/ecosystemClient';
import { AssetSortDropdown } from '../components/storefront/filters/AssetSortInput';
import { useThemesStorefrontRouteParams } from '../hooks/useStorefrontRouteParams';
import { ThemeFilterFields } from '../components/storefront/filters/FilterFields';
import MobileFilterPanel from '../components/storefront/filters/MobileFilterPanel';
import ResultsFilters from '../components/storefront/resultsFilters/ResultsFilters';
import RouterAssetCard from '../storefront/RouterAssetCard';
import StorefrontDocumentHead from '../components/storefront/documentHead/StorefrontDocumentHead';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import StorefrontViewToggle from '../components/storefront/StorefrontViewToggle';
import { ThemeQuery, DEFAULT_THEME_SORT_VALUE, THEME_QUERY_FILTER_ORDER } from '../records/ThemeQuery';
import ThemeSearchResponse from '../records/ThemeSearchResponse';
/* @ts-expect-error module is untyped */

import UsageViewTracker from '../components/tracking/UsageViewTracker';
import { ThemesResultsHeaderDescription } from '../components/storefront/resultsHeader/ResultsHeaderDescription';
import { ThemesResultsHeaderTitle } from '../components/storefront/resultsHeader/ResultsHeaderTitle';
import { StorefrontPage } from '../storefront/StorefrontPage';
import BrowseBar from '../components/storefront/browse/BrowseBar';
const EMPTY_SEARCH_RESPONSE = new ThemeSearchResponse();
const NAV_BAR_HEIGHT_INT = parseInt(globalNavHeight, 10);

const getFullPathnameFromLocation = ({
  pathname,
  search
}) => {
  return `${pathname}${search}`;
};

const getPageviewKey = (pathname, searchTerm) => {
  return `pageview::${pathname}${searchTerm ? `?${searchTerm}` : ''}`;
};

const getFilterViewKey = (pathname, themeQuery) => {
  return `filterView::${pathname}?${toQueryString(themeQuery)}`;
};

const maybeGetI18nComponentText = component => {
  if (!(component instanceof FormattedMessage)) return null;
  const {
    message: componentMessage,
    options: componentOptions
  } = component.props;
  return unescapedText(componentMessage, componentOptions);
};

const ThemesStorefrontContainer = ({
  collectionId,
  description,
  onFetchCollectionOfferingIds,
  title
}) => {
  const history = useHistory();
  const routeParams = useThemesStorefrontRouteParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const resultsView = useSelector(selectResultsView);
  const {
    pageNumber: computedPageNumber
  } = routeParams;
  const {
    pathname: currentPathname
  } = location;
  const prevPathname = usePrevious(currentPathname);
  const fullPathname = getFullPathnameFromLocation(location);
  const fullPathnameRef = useRef(fullPathname);
  const resultsFiltersRef = useRef(null);
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    }
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const passedDescriptionText = useMemo(() => maybeGetI18nComponentText(description), [description]);
  const passedTitleText = useMemo(() => maybeGetI18nComponentText(title), [title]);
  /**
   * Theme query
   */

  const themeQueryUpdatedRef = useRef(false);
  const [themeQuery, setThemeQuery] = useState(getComputedThemeQuery(routeParams, location));
  const {
    [ThemeQueryField.LIMIT]: limit,
    [ThemeQueryField.SORT_FIELDS]: sortFieldsValue,
    [ThemeQueryField.OFFSET]: computedOffset,
    [ThemeQueryField.SEARCH]: searchTerm
  } = themeQuery;
  const prevThemeQuery = usePrevious(themeQuery);
  const prevSearchTerm = usePrevious(searchTerm);
  /**
   * Theme results
   */

  const onUpdateResultsView = useCallback(value => {
    dispatch(updateResultsView(value));
  }, [dispatch]);
  const [requestState, setRequestState] = useState(RequestStates.UNINITIALIZED);
  const [searchResponse, setSearchResponse] = useState(EMPTY_SEARCH_RESPONSE);
  const {
    items: results,
    total
  } = searchResponse;
  const resultsSize = results.size;
  const computedPageCount = useMemo(() => {
    return Math.ceil(total / limit);
  }, [limit, total]);
  const computedResultsCount = useMemo(() => {
    if (!total) {
      return null;
    }

    const start = computedOffset + 1;
    return {
      start: total ? start : 0,
      end: computedOffset + Math.min(resultsSize, limit),
      total
    };
  }, [computedOffset, limit, resultsSize, total]);
  /**
   * Usage tracking
   */

  const {
    trackEvent,
    trackInteraction,
    trackBeforeUnloadInteraction,
    trackFilterView,
    trackPageview,
    setDefaultEventProperties
  } = useContext(StorefrontUsageTrackerContext);
  const getDefaultEventProperties = useCallback(() => {
    return Object.assign({}, toUsageEventProperties(themeQuery), {
      screen: 'storefront',
      type: 'theme',
      category: collectionId ? `collection::${collectionId}` : undefined,
      pageNumber: computedPageNumber,
      totalCount: total
    });
  }, [collectionId, computedPageNumber, themeQuery, total]); // Set default event properties

  useEffect(() => {
    setDefaultEventProperties(getDefaultEventProperties());
  }, [getDefaultEventProperties, setDefaultEventProperties]); // Reset default event properties on unmount

  useEffect(() => () => setDefaultEventProperties(null), [setDefaultEventProperties]); // Cache & update keys to track page/filter views

  const [pageviewKey, setPageviewKey] = useState(getPageviewKey(currentPathname, searchTerm));
  useEffect(() => {
    setPageviewKey(getPageviewKey(currentPathname, searchTerm));
  }, [currentPathname, searchTerm]);
  const [filterViewKey, setFilterViewKey] = useState(null);
  useEffect(() => {
    if (currentPathname === prevPathname && searchTerm === prevSearchTerm && !themeQuery.equals(prevThemeQuery)) {
      setFilterViewKey(getFilterViewKey(currentPathname, themeQuery));
    }
  }, [currentPathname, prevPathname, prevSearchTerm, prevThemeQuery, searchTerm, themeQuery]);
  /**
   * Callback handlers
   */

  const handleThemeQueryChange = useCallback(value => {
    themeQueryUpdatedRef.current = true;
    setThemeQuery(value);
  }, []);
  const handlePageChange = useCallback(({
    target: {
      value
    }
  }) => {
    const newComputedOffset = getOffsetFromPageNumber(value, limit);
    handleThemeQueryChange(themeQuery.set(ThemeQueryField.OFFSET, newComputedOffset));
  }, [handleThemeQueryChange, limit, themeQuery]);
  const handleFilterUpdate = useCallback((field, value, shouldTrackEvent = true) => {
    handleThemeQueryChange(previousThemeQuery => {
      return previousThemeQuery.set(field, value).set('offset', 0);
    });

    if (shouldTrackEvent) {
      let eventValue = value;

      if (value) {
        if (value.constructor.name === 'Object') {
          eventValue = Object.entries(value).map(([k, v]) => `${k}=${v}`);
        } else if (!Array.isArray(value)) {
          eventValue = [value];
        }
      }

      trackEvent('storefront-change-filter', {
        action: 'change filter',
        filter: field,
        value: eventValue
      });
    }
  }, [handleThemeQueryChange, trackEvent]);
  const handleApplyFilters = useCallback(filters => {
    setIsFilterPanelOpen(false);
    const updatedThemeQuery = produce(themeQuery, draft => {
      draft.offset = 0;
      return Object.assign(draft, filters);
    });
    handleThemeQueryChange(updatedThemeQuery);
    resultsFiltersRef.current.focus();
  }, [themeQuery, handleThemeQueryChange, setIsFilterPanelOpen]);
  const handleClearFilters = useCallback(() => {
    trackInteraction('clear all filters');
    const updatedThemeQuery = produce(themeQuery, draft => {
      THEME_QUERY_FILTER_ORDER.forEach(field => {
        delete draft[field];
      });
      draft.offset = 0;
    });
    handleThemeQueryChange(new ThemeQuery(updatedThemeQuery));
  }, [handleThemeQueryChange, themeQuery, trackInteraction]);
  const handleClearFilterValue = useCallback((field, value) => {
    const queryValue = themeQuery.get(field);
    const newValue = Array.isArray(queryValue) ? queryValue.filter(v => v !== value) : null;
    handleFilterUpdate(field, newValue, false);
    let eventValue = value;

    if (value) {
      if (value.constructor.name === 'Object') {
        eventValue = Object.entries(value).map(([k, v]) => `${k}=${v}`);
      } else if (!Array.isArray(value)) {
        eventValue = [value];
      }
    }

    trackEvent('storefront-close-filter-tag', {
      action: 'close filter tag',
      filter: field,
      value: eventValue
    });
  }, [themeQuery, handleFilterUpdate, trackEvent]);
  const handleSortChange = useCallback(value => {
    handleFilterUpdate('sortFields', [value], false);
    trackInteraction('change sort', {
      sortField: value
    });
  }, [handleFilterUpdate, trackInteraction]);
  /**
   * Results scroller
   */

  const resultsRef = useRef(null);
  const [scrollToResultsOpacity, setScrollToResultsOpacity] = useState(0);
  const [scrollToResultsFixed, setScrollToResultsFixed] = useState(true);
  const detectResultsVisible = useCallback(() => {
    const resultsEl = findDOMNode(resultsRef.current);
    if (!(resultsEl instanceof Element)) return;
    if (!resultsEl.parentElement) return;
    setScrollToResultsFixed(resultsEl.parentElement.getBoundingClientRect().bottom > window.innerHeight);
    const rect = resultsEl.getBoundingClientRect();
    setScrollToResultsOpacity((NAV_BAR_HEIGHT_INT - rect.bottom) / 200);
  }, [resultsRef]);
  useEffect(() => {
    window.addEventListener('scroll', detectResultsVisible);
    return () => {
      window.removeEventListener('scroll', detectResultsVisible);
    };
  }, [detectResultsVisible]);
  useEffect(() => detectResultsVisible());
  useEffect(() => {
    if (requestState === RequestStates.SUCCEEDED && computedPageCount > 0 && computedPageNumber > computedPageCount) {
      history.replace(getPaginatedPathname(currentPathname, 1));
    }
  }, [computedPageCount, computedPageNumber, history, currentPathname, requestState]);
  useEffect(() => {
    if (themeQueryUpdatedRef.current) {
      setSearchResponse(EMPTY_SEARCH_RESPONSE);
      setRequestState(RequestStates.UNINITIALIZED);
      const nextLocation = getLocationFromThemeQuery(themeQuery, location, collectionId != null);
      history.push(nextLocation);
      fullPathnameRef.current = getFullPathnameFromLocation(nextLocation);
      themeQueryUpdatedRef.current = false;
    }
  }, [collectionId, history, location, themeQuery]);
  useEffect(() => {
    if (fullPathname !== fullPathnameRef.current) {
      setSearchResponse(EMPTY_SEARCH_RESPONSE);
      setRequestState(RequestStates.UNINITIALIZED);
      setThemeQuery(getComputedThemeQuery(routeParams, location));
      fullPathnameRef.current = getFullPathnameFromLocation(location);
    }
  }, [fullPathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isUninitialized(requestState)) {
      setRequestState(RequestStates.PENDING);
      let request;

      if (onFetchCollectionOfferingIds) {
        request = onFetchCollectionOfferingIds().then(offeringIds => {
          if (!offeringIds.length) {
            return Promise.resolve([{
              total: 0,
              items: []
            }]);
          }

          const searchQuery = toSearchQuery(themeQuery.set(ThemeQueryField.OFFERING_IDS, offeringIds));
          return searchThemes([searchQuery]);
        });
      } else {
        const searchQuery = toSearchQuery(themeQuery);
        request = searchThemes([searchQuery]);
      }

      request.then(([resp]) => {
        setSearchResponse(new ThemeSearchResponse(resp));
        setRequestState(RequestStates.SUCCEEDED);
      }).catch(error => {
        console.error('error fetching themes\n', error);
        setSearchResponse(EMPTY_SEARCH_RESPONSE);
        setRequestState(RequestStates.FAILED);
      });
    }
  }, [onFetchCollectionOfferingIds, requestState, themeQuery]);
  const renderedSearchResults = useMemo(() => {
    if (!results.size) {
      return /*#__PURE__*/_jsx(UIFlex, {
        direction: "column",
        children: searchTerm ? /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.storefront.header.search.noResults",
              options: {
                searchTerm
              }
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.storefront.header.search.noSearchResultsSubtext"
          })]
        }) : /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.storefront.header.noResults"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.storefront.header.search.noFilterResultsSubtext"
          })]
        })
      });
    }

    return results.map((result, index) => /*#__PURE__*/_jsx(RouterAssetCard, {
      onClick: () => {
        trackBeforeUnloadInteraction('click result item', {
          value: result.slug,
          itemIndex: index,
          totalResults: resultsSize
        });
      },
      onTrackInteraction: trackInteraction,
      onTrackPageview: trackPageview,
      result: result.toJS(),
      view: resultsView
    }, `result-${index}`));
  }, [results, resultsSize, searchTerm, resultsView, trackBeforeUnloadInteraction, trackInteraction, trackPageview]);
  const renderedScrollToResults = useMemo(() => {
    const resultsEl = findDOMNode(resultsRef.current);
    if (!(resultsEl instanceof HTMLElement) || results.size === 0) return null;
    return /*#__PURE__*/_jsx(ScrollToResults, {
      containerWidth: resultsEl.offsetWidth,
      opacity: scrollToResultsOpacity,
      fixed: scrollToResultsFixed
    });
  }, [results, scrollToResultsOpacity, scrollToResultsFixed]);
  const renderedViewToggle = useMemo(() => /*#__PURE__*/_jsx(StorefrontViewToggle, {
    activeView: resultsView,
    onChange: onUpdateResultsView
  }), [resultsView, onUpdateResultsView]);
  const screenSize = useScreenSize();
  const isXSScreen = screenSize === ScreenSizes.XS;
  const gridColumns = isXSScreen ? 1 : 2;
  const columns = useMemo(() => resultsView === 'list' ? 1 : gridColumns, [resultsView, gridColumns]);
  const themeFilters = themeQuery.toJS();
  const signupUrl = getCmsFreeSignupUrl();

  const sidebar = /*#__PURE__*/_jsx(MediaQuery, {
    minScreenSize: ScreenSizes.LG,
    children: /*#__PURE__*/_jsx(UIGridItem, {
      size: {
        lg: 3
      },
      children: /*#__PURE__*/_jsx(ThemeFilterFields, {
        filters: themeFilters,
        onFilterChange: handleFilterUpdate
      })
    })
  });

  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "storefront-container",
    children: [requestState === RequestStates.SUCCEEDED && /*#__PURE__*/_jsx(NavMarker, {
      name: "THEMES_LOAD"
    }), /*#__PURE__*/_jsx(StorefrontDocumentHead, {
      description: passedDescriptionText,
      pageCount: computedPageCount,
      requestState: requestState,
      routeParams: routeParams,
      searchTerm: searchTerm,
      title: passedTitleText,
      total: total
    }), /*#__PURE__*/_jsx(StorefrontPage, {
      navigation: /*#__PURE__*/_jsx(BrowseBar, {
        previewCollectionId: collectionId
      }),
      sidebar: sidebar,
      children: /*#__PURE__*/_jsxs(UIGridItem, {
        size: {
          xs: 12,
          lg: 9
        },
        children: [/*#__PURE__*/_jsx(PublicAppContext, {
          children: /*#__PURE__*/_jsx(UIBanner, {
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.storefront.signUpBanner.title"
            }),
            children: /*#__PURE__*/_jsxs(UIFlex, {
              align: "center",
              direction: isMobileView ? 'column' : 'row',
              justify: "start",
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-assets-ui.storefront.signUpBanner.message"
              }), /*#__PURE__*/_jsx(UIButton, {
                className: isMobileView ? '' : 'm-left-2',
                "data-test-id": "storefront-signup-banner-button",
                href: signupUrl,
                onClick: () => trackBeforeUnloadInteraction('click cms sign up link'),
                size: "extra-small",
                style: {
                  display: 'block',
                  minWidth: '70px'
                },
                use: "primary-white",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "marketplace-assets-ui.storefront.signUpBanner.signUp"
                })
              })]
            })
          })
        }), /*#__PURE__*/_jsx(StorefrontResultsHeader, {
          className: "m-top-6",
          description: /*#__PURE__*/_jsx(ThemesResultsHeaderDescription, {
            description: description,
            routeParams: routeParams,
            searchTerm: searchTerm || undefined
          }),
          renderedResultsFilters: /*#__PURE__*/_jsx(ResultsFilters, {
            filters: toFilterTags(themeQuery),
            onClearFilter: handleClearFilterValue,
            onClearAllFilters: handleClearFilters,
            onOpenFilterPanel: () => setIsFilterPanelOpen(true),
            resultsFiltersRef: resultsFiltersRef
          }),
          renderedSortDropdown: /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.LG,
            children: /*#__PURE__*/_jsx(AssetSortDropdown, {
              onSelect: value => handleSortChange(value),
              value: sortFieldsValue[0] || DEFAULT_THEME_SORT_VALUE
            })
          }),
          resultsCount: computedResultsCount,
          routeParams: routeParams,
          searchTerm: searchTerm || undefined,
          title: /*#__PURE__*/_jsx(ThemesResultsHeaderTitle, {
            routeParams: routeParams,
            searchTerm: searchTerm || undefined,
            title: title
          }),
          viewToggle: renderedViewToggle
        }), requestState === RequestStates.PENDING && /*#__PURE__*/_jsx(UILoadingSpinner, {
          layout: "centered",
          minHeight: 150
        }), requestState === RequestStates.SUCCEEDED && /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsxs("div", {
            ref: resultsRef,
            "data-test-id": "storefront-results-container",
            children: [/*#__PURE__*/_jsx(UICardGrid, {
              gutterSize: 16,
              columns: columns,
              children: renderedSearchResults
            }), computedPageCount > 1 && /*#__PURE__*/_jsx(UIPaginator, {
              className: "m-top-8",
              onPageChange: handlePageChange,
              page: computedPageNumber,
              pageCount: computedPageCount
            })]
          }), renderedScrollToResults]
        })]
      })
    }), /*#__PURE__*/_jsx(UsageViewTracker, {
      active: isFulfilled(requestState),
      onTrackView: trackPageview
    }, pageviewKey), filterViewKey && /*#__PURE__*/_jsx(UsageViewTracker, {
      active: isFulfilled(requestState),
      onTrackView: trackFilterView
    }, filterViewKey), /*#__PURE__*/_jsx(MobileFilterPanel, {
      filters: themeFilters,
      offeringType: "THEME",
      onApplyFilters: handleApplyFilters,
      onClearFilters: handleClearFilters,
      onClose: () => {
        setIsFilterPanelOpen(false);
        resultsFiltersRef.current.focus();
      },
      open: isFilterPanelOpen
    })]
  });
};

export default ThemesStorefrontContainer;